import type { GateId, GateLevel } from '@hoopoe/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { Save } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useReauth } from '../components/auth/reauth';
import { ErrorState } from '../components/guidance/error-state';
import { LearnMore } from '../components/guidance/learn-more';
import { PageHeader } from '../components/layout/app-shell';
import { Button } from '../components/ui/button';
import { ApiError, api } from '../lib/api';
import type { GateSettingsView } from '../lib/gates';

// Admin → Report quality gates: the eight checks a full report runs before an export (the report bar's rules), each
// off, advisory, blocking, or blocking only when a named person is in pending proceedings.

export const Route = createFileRoute('/admin/report-gates')({ component: GatesPage });

function GatesPage() {
  const qc = useQueryClient();
  const { withReauth } = useReauth();
  const q = useQuery({ queryKey: ['admin', 'report-gates'], queryFn: () => api<GateSettingsView>('/admin/report-gates') });
  const [levels, setLevels] = useState<Record<string, GateLevel>>({});
  const [message, setMessage] = useState<{ kind: 'ok' | 'error'; text: string } | null>(null);
  useEffect(() => {
    if (q.data) setLevels(q.data.levels);
  }, [q.data]);
  const save = useMutation({
    mutationFn: (next: Record<string, GateLevel>) =>
      withReauth(() => api<GateSettingsView>('/admin/report-gates', { method: 'PUT', body: { levels: next } })),
    onSuccess: v => {
      qc.setQueryData(['admin', 'report-gates'], v);
      setMessage({ kind: 'ok', text: 'Saved. The next export and gate check use these levels.' });
    },
    onError: e => {
      if (e instanceof ApiError && e.code === 'reauth_cancelled') return;
      setMessage({ kind: 'error', text: e instanceof ApiError ? e.message : 'Something went wrong. Try again.' });
    },
  });

  return (
    <div className="space-y-6">
      <PageHeader
        title="Report quality gates"
        description="The checks a full report runs before it is exported, and what each one does when it finds something."
      />
      {q.isError ? (
        <ErrorState error={q.error} title="The gate levels couldn’t be loaded" onRetry={() => q.refetch()} />
      ) : !q.data ? (
        <div aria-busy="true" className="h-40 animate-pulse rounded-md bg-muted">
          <span className="sr-only">Loading the gates…</span>
        </div>
      ) : (
        <form
          className="space-y-4"
          onSubmit={e => {
            e.preventDefault();
            setMessage(null);
            save.mutate(levels);
          }}
        >
          <p className="max-w-prose text-sm text-muted-foreground">
            <strong>Advisory</strong> lists the finding for the author; <strong>blocking</strong> holds the Word or PDF export until it is
            cleared; <strong>blocking in pending proceedings</strong> holds it only when the sentence concerns a person a legal action
            targets with no outcome recorded yet, and lists it otherwise. <LearnMore slug="quality-gates" />
          </p>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-start">
                <th scope="col" className="py-1 text-start font-medium">
                  Gate
                </th>
                <th scope="col" className="py-1 text-start font-medium">
                  Rule
                </th>
                <th scope="col" className="py-1 text-start font-medium">
                  Level
                </th>
              </tr>
            </thead>
            <tbody>
              {q.data.gates.map(g => (
                <tr key={g.id} className="border-t align-top">
                  <th scope="row" className="py-2 pe-3 text-start font-medium">
                    <label htmlFor={`gate-${g.id}`}>{g.title}</label>
                  </th>
                  <td className="py-2 pe-3 text-muted-foreground">{g.rule}</td>
                  <td className="py-2">
                    <select
                      id={`gate-${g.id}`}
                      className="h-9 w-full max-w-xs rounded-md border border-input bg-surface px-2 text-sm"
                      value={levels[g.id] ?? q.data?.defaults[g.id as GateId]}
                      onChange={e => setLevels(l => ({ ...l, [g.id]: e.target.value as GateLevel }))}
                    >
                      {q.data?.levelLabels.map(o => (
                        <option key={o.level} value={o.level}>
                          {o.label}
                          {o.level === q.data?.defaults[g.id as GateId] ? ' (default)' : ''}
                        </option>
                      ))}
                    </select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="flex flex-wrap items-center gap-2">
            <Button type="submit" disabled={save.isPending}>
              <Save aria-hidden="true" /> {save.isPending ? 'Saving…' : 'Save levels'}
            </Button>
            {message && (
              <p
                role={message.kind === 'error' ? 'alert' : 'status'}
                className={`text-sm ${message.kind === 'error' ? 'text-danger-foreground' : 'text-muted-foreground'}`}
              >
                {message.text}
              </p>
            )}
          </div>
        </form>
      )}
    </div>
  );
}
